/**
 * Teacher → Bible Integration.
 *
 * Phase 1: list Bible plans available to this tenant (or public) so the
 * teacher knows what they can assign. The actual "assign to class" flow
 * uses the existing /api/admin/bible/scope/plans/{plan_id}/assign endpoint
 * via the AssignPlanDialog component — we render a link to it from each row
 * rather than re-implementing the picker here.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookMarked, Loader2, ExternalLink, Globe } from 'lucide-react';
import { portalApi } from './teacherApi';

export const TeacherBible = ({ tenantId }) => {
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['teacher-bible-plans'],
    queryFn: () => portalApi('/teacher/bible/plans'),
  });

  return (
    <div className="space-y-3" data-testid="teacher-bible-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <BookMarked className="h-5 w-5 text-indigo-500" /> Bible plans <Badge variant="outline">{plans.length}</Badge>
        </h2>
        <Button variant="outline" size="sm" asChild>
          <a href={`/admin/schools/${tenantId}#bible`} target="_blank" rel="noopener noreferrer">
            Open Bible scope <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </a>
        </Button>
      </div>

      {isLoading ? (
        <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</div>
      ) : plans.length === 0 ? (
        <div className="text-center text-slate-500 p-8 border border-dashed rounded">
          No Bible reading plans visible. Ask your school admin to add one via the Bible scope module.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {plans.map((p) => (
            <Card key={p.id} data-testid={`teacher-bible-plan-${p.id}`}>
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-sm">{p.name || p.title}</h3>
                  {(p.is_public || p.public) && <Badge className="bg-emerald-100 text-emerald-700 gap-1"><Globe className="h-3 w-3" /> public</Badge>}
                </div>
                <p className="text-xs text-slate-500 line-clamp-3">{p.description || '—'}</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {p.duration_days && <Badge variant="outline" className="text-[10px]">{p.duration_days} days</Badge>}
                  {p.book_count && <Badge variant="outline" className="text-[10px]">{p.book_count} books</Badge>}
                  {p.chapter_count && <Badge variant="outline" className="text-[10px]">{p.chapter_count} chapters</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-500 pt-2 border-t">
        Tip: use <strong>Admin → Bible scope → Plans</strong> to assign any plan above to a class, an audience
        (all_students), or specific students. Their progress feeds into the dashboard&apos;s
        <code className="ml-1">average_attendance_pct</code>-style metrics.
      </p>
    </div>
  );
};

export default TeacherBible;
